import { Component, computed, effect, inject, input, model, output, Signal } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { BasicDialogComponent } from 'src/app/BaseDialog/basic-dialog/basic-dialog.component';
import { BusinessFinanceAccountListComponent } from 'src/app/business-finance-account-list/business-finance-account-list.component';
import { DialogItem } from 'src/app/dialog-item';
import { MatTableController } from 'src/app/mat-table-controller';
import { Enums } from 'src/app/Shared/enums';
import { SharedService } from 'src/app/Shared/shared-service.service';
import { Business } from 'src/model/business';
import { BusinessStore } from 'src/model/business/business-store';
import { FinanceAccount } from 'src/model/financeAccount.model';

interface BusinessWithBranch extends Business {
	BranchCount: number;
	FACount: number;
}

@Component({
	selector: 'app-business-list',
	templateUrl: './business-list.component.html',
	styleUrl: './business-list.component.scss',
	providers: [MatTableController],
	standalone: false
})
export class BusinessListComponent {
	protected sharedService = inject(SharedService);
	protected enums = inject(Enums);
	protected table = inject(MatTableController<BusinessWithBranch>);
	private store = inject(BusinessStore);

	businessList = this.store.businessList;
	branchList = this.store.branchList;
	faList = model.required<FinanceAccount[]>();
	searchText = input<string>();

	businessFAList = computed(() => {
		return this.faList().reduce(
			(result, current) => {
				current.BusinessIds?.forEach(b => {
					if (!result[b]) result[b] = [];
					result[b].push(current);
				});
				return result;
			},
			{} as Record<number, FinanceAccount[]>
		);
	});
	l = computed(() => this.sharedService.groupByNumKey(this.branchList(), 'BusinessId'));
	businessWithBranchList = computed<BusinessWithBranch[]>(() => {
		const biz = this.businessList();
		return biz?.length > 0 ?
			biz.map(t => {
				const b = {
					...t,
					BranchCount: this.l()[t.Id]?.length ?? 0,
					FACount: this.businessFAList()[t.Id]?.length ?? 0
				}
				return b;
			})
		:	[];
	});

	showBranchOfBusiness = output<string>();

	isBoss = computed(() => this.sharedService.isBossAccount());
	showLicense = false;
	dataSource: MatTableDataSource<BusinessWithBranch>;
	displayedColumns: Signal<string[]> = computed(() => this.isBoss() ? [...this.table.columnDefs(), 'Actions'] : this.table.columnDefs());

	constructor() {
		this.table.config.set([
			{ columnDef: 'Name', header: 'Name', canHide: false },
			// { columnDef: 'Id', header: 'ID' },
			{ columnDef: 'Tin', header: 'TIN No.' },
			{ columnDef: 'Brn', header: 'BRN No.' },
			{ columnDef: 'OldRegNo', header: 'Old RegNo.', show: false },
			{ columnDef: 'Sst', header: 'SST No.' },
			{ columnDef: 'Ttx', header: 'TTX No.', show: false },
			{ columnDef: 'Directors', header: 'Directors', show: false },
			{ columnDef: 'BusinessType', header: 'Business Type', show: false },
			{ columnDef: 'SsmExpiryDate', header: 'SSM Expiry Date', type: 'date', format: 'dd MMM yyyy' },
			{ columnDef: 'RequireConsentForm', header: 'Consent', type: 'boolean', show: false },
			{ columnDef: 'YearEndMonth', header: 'Year End', format: v => this.sharedService.formatMonth(v, 'long'), show: false },
			{ columnDef: 'BranchCount', header: 'Branch', onClick: row => this.goToBranch(row.Name) },
			{ columnDef: 'FACount', header: 'Finance Accounts', onClick: row => this.openFinanceAccounts(row) },
			{ columnDef: 'Remark', header: 'Remark' }
		]);

		effect(() => {
			this.refreshDataSource();
		});
	}

	openFinanceAccounts(business: BusinessWithBranch) {
		const modalRef = this.sharedService.openModal(BasicDialogComponent);
		const i = modalRef.componentInstance as BasicDialogComponent;
		i.title = 'Finance Accounts';
		i.injectContent = [
			new DialogItem(BusinessFinanceAccountListComponent, { allFaList: this.faList(), faList: this.businessFAList()[business.Id] ?? [], business: business }),
		];
		modalRef.result.then((result: FinanceAccount) => {
			if (result) this.faList.update(list => list.filter(f => f.Id !== result.Id));
		});
	}

	refreshDataSource() {
		this.table.rawData.set([{ items: this.businessWithBranchList() }]);
		this.table.filterQuery.set(this.searchText() ?? '');
	}

	goToBranch(name: string) {
		this.showBranchOfBusiness.emit(name);
	}

	businessToUpdate(business: Business) {
		this.store.updateBusiness(business);
	}

	branchToAdd(Id: number) {
		this.store.addBusinessBranch(Id);
	}
}
